import {Async, fromResult, Loaded, toEither, Empty} from 'Core/models/Async';
import {unwrapValidatableWithDefault} from 'Core/models/Valid';
import * as E from 'fp-ts/lib/Either';
import {constant, flow} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import {getEitherValue} from 'Shared/components/ValidatablePhoneInput';
import {createAction} from 'Shared/utilities/actionCreator';
import {
  EmptyEnrollTrackUser,
  EnrollTrackUser,
  InvalidEnrollTrackUser,
  validateEnrollTrackUser,
  ValidEnrollTrackUser,
} from 'Users/models/EnrollTrackUser';
import {RoleBase} from 'Users/api';

export enum EnrollTrackUserActionType {
  GET_USER = 'enrollTrackUser/GET_USER',
  GET_ROLES = 'enrollTrackUser/GET_ROLES',
  LOAD_ROLES = 'enrollTrackUser/LOAD_ROLES',
  VALIDATE_EMAIL = 'enrollTrackUser/VALIDATE_EMAIL',
  VALIDATE_USERNAME = 'enrollTrackUser/VALIDATE_USERNAME',
  VALIDATE_PHONE = 'enrollTrackUser/VALIDATE_PHONE',
  CHANGE_USER = 'enrollTrackUser/CHANGE_USER',
  CLEAR_USER = 'enrollTrackUser/CLEAR_USER',
  SAVE_USER = 'enrollTrackUser/SAVE_USER',
  SET_PHONE = 'enrollTrackUser/SET_PHONE',
  SET_USERNAME = 'enrollTrackUser/SET_USERNAME',
  SET_EMAIL = 'enrollTrackUser/SET_EMAIL',
}

export const getRoles = () => createAction(EnrollTrackUserActionType.GET_ROLES);
export const loadRoles = (roles: Async<RoleBase[]>) => createAction(EnrollTrackUserActionType.LOAD_ROLES, {roles});
export const validateEmail = (email: string) => createAction(EnrollTrackUserActionType.VALIDATE_EMAIL, email);
export const validatePhone = (phone: string) => createAction(EnrollTrackUserActionType.VALIDATE_PHONE, phone);
export const validateUsername = (username: string) =>
  createAction(EnrollTrackUserActionType.VALIDATE_USERNAME, username);
export const getUser = (userId: string) => createAction(EnrollTrackUserActionType.GET_USER, {userId});
export const clearUser = () => createAction(EnrollTrackUserActionType.CLEAR_USER);
export const changeUser = (user: Async<EnrollTrackUser>) => createAction(EnrollTrackUserActionType.CHANGE_USER, user);
export const setUsername = (username: InvalidEnrollTrackUser['username']) =>
  createAction(EnrollTrackUserActionType.SET_USERNAME, {username});
export const setPhone = (phone: InvalidEnrollTrackUser['phoneNumber']) =>
  createAction(EnrollTrackUserActionType.SET_PHONE, {phone});
export const setEmail = (email: InvalidEnrollTrackUser['email']) =>
  createAction(EnrollTrackUserActionType.SET_EMAIL, {email});
export const save = (user: ValidEnrollTrackUser, userId: O.Option<string>) =>
  createAction(EnrollTrackUserActionType.SAVE_USER, {user, userId});

type EnrollTrackUserState = {
  user: Async<EnrollTrackUser>;
  original: O.Option<Async<EnrollTrackUser>>;
  roles: Async<RoleBase[]>;
};

type EnrollTrackUserActions =
  | ReturnType<typeof clearUser>
  | ReturnType<typeof changeUser>
  | ReturnType<typeof loadRoles>
  | ReturnType<typeof setEmail>
  | ReturnType<typeof setPhone>
  | ReturnType<typeof setUsername>;

const DEFAULT_STATE = {
  user: new Loaded(EmptyEnrollTrackUser),
  original: O.none,
  roles: new Empty(),
};

export default (state: EnrollTrackUserState = DEFAULT_STATE, action: EnrollTrackUserActions): EnrollTrackUserState => {
  switch (action.type) {
    case EnrollTrackUserActionType.SET_EMAIL: {
      const newUser = pipe(
        state.user,
        toEither,
        E.bimap(
          constant(state.user),
          flow(
            unwrapValidatableWithDefault<InvalidEnrollTrackUser | ValidEnrollTrackUser>(),
            Lens.fromProp<InvalidEnrollTrackUser | ValidEnrollTrackUser>()('email').set(action.payload.email),
            validateEnrollTrackUser,
            fromResult
          )
        ),
        getEitherValue
      );

      return {
        ...state,
        user: newUser,
      };
    }
    case EnrollTrackUserActionType.LOAD_ROLES:
      return {
        ...state,
        roles: action.payload.roles,
      };
    case EnrollTrackUserActionType.SET_USERNAME: {
      const newUser = pipe(
        state.user,
        toEither,
        E.bimap(
          constant(state.user),
          flow(
            unwrapValidatableWithDefault<InvalidEnrollTrackUser | ValidEnrollTrackUser>(),
            Lens.fromProp<InvalidEnrollTrackUser | ValidEnrollTrackUser>()('username').set(action.payload.username),
            validateEnrollTrackUser,
            fromResult
          )
        ),
        getEitherValue
      );

      return {
        ...state,
        user: newUser,
      };
    }
    case EnrollTrackUserActionType.SET_PHONE: {
      const newUser = pipe(
        state.user,
        toEither,
        E.bimap(
          constant(state.user),
          flow(
            unwrapValidatableWithDefault<InvalidEnrollTrackUser | ValidEnrollTrackUser>(),
            Lens.fromProp<InvalidEnrollTrackUser | ValidEnrollTrackUser>()('phoneNumber').set(action.payload.phone),
            validateEnrollTrackUser,
            fromResult
          )
        ),
        getEitherValue
      );

      return {
        ...state,
        user: newUser,
      };
    }
    case EnrollTrackUserActionType.CLEAR_USER:
      return DEFAULT_STATE;
    case EnrollTrackUserActionType.CHANGE_USER:
      return Lens.fromProp<EnrollTrackUserState>()('user').set(action.payload)(state);
    default:
      return state;
  }
};
